import { ONE_HOUR_MILLISECONDS, ONE_MINUTE_MILLISECONDS } from '../../dateTime';
import { queryClient } from '../queryClient';
import { ChannelProgramsQueryKey, queryKeys } from '../queryKeys';

import { RhinoEPGProgram, RhinoProgram, RhinoProgramPlaceholder } from './type';

export function getChannelProgramsFromCache(
  channelId: number,
  withPlaceholders?: { startDate: number; endDate: number },
) {
  return denormalizePrograms(getNormalizedChannelProgramsFromCache(channelId), withPlaceholders);
}

// programs are stored in cache in chunks by day, this function returns all days of a particular channel
function getNormalizedChannelProgramsFromCache(channelId: number) {
  return queryClient.getQueriesData({ queryKey: queryKeys.channelProgramsAllDays(channelId) }) as Array<
    [ChannelProgramsQueryKey, RhinoProgram[] | undefined]
  >;
}

// TODO Cover with unit tests
// transforms array of days(with a list of programs in every day) to an array of programs of a particular channel
// if `withPlaceholder` is defined, all empty spaces of not loaded yet programs are filled with placeholders
function denormalizePrograms(
  normalizedPrograms: ReturnType<typeof getNormalizedChannelProgramsFromCache>,
  withPlaceholders?: { startDate: number; endDate: number },
): RhinoEPGProgram[] {
  // @ts-expect-error TODO Fix
  normalizedPrograms.sort(compareDaysFromCache);

  const programs: RhinoEPGProgram[] = [];

  normalizedPrograms.forEach((chunk) => {
    if (chunk[1] !== undefined && chunk[1].length > 0) {
      // we can have duplicates when fetching programs, remove one of duplicates if found
      if (programs.length > 0 && programs[programs.length - 1].id === chunk[1][0].id) {
        programs.pop();
      }

      programs.push(...chunk[1]);
    }
  });

  if (withPlaceholders === undefined) return programs;

  const { startDate, endDate } = withPlaceholders;

  if (programs.length === 0) return getProgramPlaceholders(startDate, endDate);

  const programsWithPlaceholders: RhinoEPGProgram[] = [];

  for (let i = 0; i < programs.length; i++) {
    const program = programs[i];
    if (i === 0) {
      if (program.startDate > startDate) {
        programsWithPlaceholders.push(...getProgramPlaceholders(startDate, program.startDate));
      }
    } else {
      const previousProgram = programs[i - 1];
      if (program.startDate > previousProgram.endDate) {
        programsWithPlaceholders.push(...getProgramPlaceholders(previousProgram.endDate, program.startDate));
      }
    }

    programsWithPlaceholders.push(program);
  }

  if (endDate > programs[programs.length - 1].endDate) {
    programsWithPlaceholders.push(...getProgramPlaceholders(programs[programs.length - 1].endDate, endDate));
  }

  return programsWithPlaceholders;
}

// TODO Cover with unit tests
// used to sort programs of day chunks from cache by date
function compareDaysFromCache(d1: [ChannelProgramsQueryKey, unknown], d2: [ChannelProgramsQueryKey, unknown]) {
  const day1Array = d1[0][2].split('-');
  const day2Array = d2[0][2].split('-');

  const year1 = Number(day1Array[0]);
  const year2 = Number(day2Array[0]);
  if (year1 < year2) return -1;
  if (year1 > year2) return 1;

  const month1 = Number(day1Array[1]);
  const month2 = Number(day2Array[1]);
  if (month1 < month2) return -1;
  if (month1 > month2) return 1;

  const day1 = Number(day1Array[2]);
  const day2 = Number(day2Array[2]);
  if (day1 < day2) return -1;
  if (day1 > day2) return 1;
}

// TODO Cover with unit tests
// generates sequence of placeholders from `startDate` to `endDate`
export function getProgramPlaceholders(startDate: number, endDate: number): RhinoProgramPlaceholder[] {
  // We don't want placeholders less than a minute, skip generation in this case
  if (endDate - startDate < ONE_MINUTE_MILLISECONDS) return [];

  let currentPlaceholderStartDate = startDate;
  let currentPlaceholderEndDate = startDate;
  const placeholdersArray: RhinoProgramPlaceholder[] = [];

  do {
    currentPlaceholderEndDate = Math.min(
      endDate,
      currentPlaceholderEndDate + Math.floor((1.25 + Math.random()) * ONE_HOUR_MILLISECONDS),
    );

    placeholdersArray.push({
      // id should be stable based on startDate/endDate
      // Just sum could be non-unique, find out better way to generate?
      id: currentPlaceholderStartDate + currentPlaceholderEndDate,
      startDate: currentPlaceholderStartDate,
      endDate: currentPlaceholderEndDate,
      isPlaceholder: true,
    });

    currentPlaceholderStartDate = currentPlaceholderEndDate;
  } while (currentPlaceholderStartDate < endDate);

  return placeholdersArray;
}
